import { useCallback, useMemo } from "react";
import { useQuantities } from "./useQuantities";
import useQuery, { useGetQueryData, useMutateBody } from "./useQuery";
import { useIndexedFailureValues } from "./useIndexedFailureValues";
import { useIndexedFailureMetadata } from "./useFailureMetadata";
import { useChannelUpdates } from "./ws/useChannelUpdates";
import call from "./call";
import { getAllFaultEventPriorities } from "diagnostics-domain/faultEvent";
import { consolidateFaultEvents } from "diagnostics-domain/faultEvent/consolidation";
import { findPowerOutputQuantity } from "diagnostics-domain/aggregationPeriod";
import { useSetups } from "./useSetups";
import useActiveAnalysesStream from "./useActiveAnalysesStream";
import { useSettings } from "./useSettings";
import { withKeyNotNull } from "diagnostics-lib/typescript";
import { comparatorBy } from "diagnostics-lib/comparator";
const PATH = "/projects/:projectId(uuid)/faultEvents" as const;

export function useRemoteFaultEvents(projectId: string) {
  const { body: faultEvents } = useQuery(PATH, {
    routeParams: { projectId },
  });
  const getCachedFaultEvents = useGetQueryData(PATH, {
    routeParams: { projectId },
  });
  const readItems = useCallback(() => getCachedFaultEvents()?.body, [getCachedFaultEvents]);
  const fetchUpdatedItems = useCallback(
    async (since: string) => {
      const { faultEvents, deletedFaultEvenIds } = (
        await call("GET", "/projects/:projectId(uuid)/faultEvents/updates/:since(date)", {
          routeParams: { projectId, since: new Date(since) },
        })
      ).body;
      return [faultEvents, deletedFaultEvenIds] as const;
    },
    [projectId],
  );
  const mutateItems = useMutateBody(PATH, { routeParams: { projectId } });

  useChannelUpdates(
    { key: "fault_events", projectId },
    { readItems, fetchUpdatedItems, mutateItems },
  );

  return faultEvents;
}

export type RemoteFaultEvent = ReturnType<typeof useRemoteFaultEvents>[number];

interface RemoteFaultEventDate {
  openedAt: Date | null;
  startsAt: Date;
  endsAt: Date;
  createdAt: Date;
  updatedAt: Date;
  closedAt: Date | null;
}
type ExtendedFaultEvent = Omit<RemoteFaultEvent, keyof RemoteFaultEventDate> & RemoteFaultEventDate;

const remoteFaultEventsCache = new WeakMap<RemoteFaultEvent[], ExtendedFaultEvent[]>();
export const useFaultEvents = (projectId: string) => {
  const remoteFaultEvents = useRemoteFaultEvents(projectId);
  const quantities = useQuantities(projectId);
  const powerQuantity = useMemo(() => findPowerOutputQuantity(quantities), [quantities]);
  const performanceIndicators = useMemo(
    () =>
      quantities
        .filter(withKeyNotNull("qoiOrder"))
        .sort(comparatorBy("qoiOrder"))
        .map(indicator => ({
          id: indicator.id,
          name: indicator.name,
          unit: indicator.unit,
          qoiOrder: indicator.qoiOrder,
        })),
    [quantities],
  );
  const indexedFailureValues = useIndexedFailureValues(projectId);
  const indexedFailureMetadata = useIndexedFailureMetadata(projectId);
  const setups = useSetups(projectId);
  const { activeAnalyses, isStreaming } = useActiveAnalysesStream(projectId);
  const analysisGroupsByDate = useMemo(
    () => new Map(activeAnalyses.map(analysis => [analysis.date, analysis.group])),
    [activeAnalyses],
  );

  const faultEventsDB = useMemo(() => {
    let remoteFaultEvents_ = remoteFaultEventsCache.get(remoteFaultEvents);
    if (!remoteFaultEvents_) {
      remoteFaultEvents_ = remoteFaultEvents.map(event => ({
        ...event,
        openedAt: event.openedAt ? new Date(event.openedAt) : null,
        startsAt: new Date(event.startsAt),
        endsAt: new Date(event.endsAt),
        createdAt: new Date(event.createdAt),
        updatedAt: new Date(event.updatedAt),
        closedAt: event.closedAt ? new Date(event.closedAt) : null,
      }));
      remoteFaultEventsCache.set(remoteFaultEvents, remoteFaultEvents_);
    }
    return remoteFaultEvents_;
  }, [remoteFaultEvents]);

  const settings = useSettings();
  const disableMediumPriority = settings.FAULT_EVENTS_DISABLE_MEDIUM_PRIORITY;
  const disableEventGroupConstraint = settings.FAULT_EVENTS_DISABLE_EVENT_GROUP_CONSTRAINT;

  const faultEvents = useMemo(
    () =>
      consolidateFaultEvents(
        projectId,
        setups,
        faultEventsDB,
        indexedFailureMetadata.failureValueStatus,
        indexedFailureValues,
        quantities,
        disableMediumPriority,
        analysisGroupsByDate,
        disableEventGroupConstraint,
      ),
    [
      faultEventsDB,
      projectId,
      setups,
      indexedFailureMetadata.failureValueStatus,
      indexedFailureValues,
      quantities,
      disableMediumPriority,
      analysisGroupsByDate,
      disableEventGroupConstraint,
    ],
  );

  return useMemo(() => {
    if (isStreaming === true)
      return {
        ...faultEvents,
        persistedEvents: faultEventsDB,
        loadingPriority: isStreaming,
      };
    return {
      loadingPriority: isStreaming,
      performanceIndicators,
      persistedEvents: faultEventsDB,
      events: getAllFaultEventPriorities(
        faultEvents.events,
        powerQuantity ? powerQuantity.id : null,
        activeAnalyses,
        indexedFailureValues,
        disableMediumPriority,
      ),
    };
  }, [
    faultEvents,
    faultEventsDB,
    activeAnalyses,
    indexedFailureValues,
    powerQuantity,
    isStreaming,
    disableMediumPriority,
    performanceIndicators,
  ]);
};

export const useFaultEvent = (projectId: string, eventId: string) => {
  const faultEvents = useFaultEvents(projectId);
  return useMemo(
    () => faultEvents.events.find(fe => fe.id === eventId),
    [eventId, faultEvents.events],
  );
};

export type FaultEvent = ReturnType<typeof useFaultEvents>["events"][number];

export interface FaultEventInfo {
  id: string;
  name: string;
  endsAt: string;
  startsAt: string;
}
